<script setup>
import { ref, computed } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import Modal from '@/Components/Modal.vue';
import { useAiRequest } from '@/composables/useAiRequest';

// Shared "Report inaccurate" affordance for every AI surface. A quiet flag button
// that opens a modal where the agent can (optionally) categorize and add context,
// then POSTs to leads.ai.feedback — which stores the report + an immutable
// reproduction snapshot so the AI team can rebuild what happened. Only shows when
// AI is enabled for the agency.
const props = defineProps({
    feature: { type: String, required: true },     // backend key: lead_summary | lead_priority_score | smart_next_step | message_drafting
    leadId: { type: [Number, String], required: true },
    subject: { type: String, default: '' },         // human label of what's being reported
    content: { type: String, default: '' },          // the displayed AI text being flagged
    wasCached: { type: Boolean, default: false },    // the displayed result came from a cache, not a fresh call
});

const aiEnabled = computed(() => usePage().props.value?.features?.['ai-enabled'] === true);

const CATEGORIES = computed(() => [
    { key: 'outdated', label: 'Wrong or outdated' },
    { key: 'irrelevant', label: 'Irrelevant' },
    { key: 'tone', label: 'Tone / wording' },
    // Only meaningful when the agent was told the result came from a cache: "the lead
    // has changed but this cached suggestion didn't" is a different defect (missed
    // invalidation) than a bad generation.
    ...(props.wasCached ? [{ key: 'stale_cache', label: 'Cache is stale (lead changed)' }] : []),
    { key: 'other', label: 'Something else' },
]);

const show = ref(false);
const comment = ref('');
const category = ref(null);
const { loading, error, reset, run } = useAiRequest();

function open() {
    comment.value = '';
    category.value = null;
    reset();
    show.value = true;
}

async function submit() {
    const res = await run(() => window.axios.post(route('leads.ai.feedback', props.leadId), {
        feature: props.feature,
        category: category.value,
        subject: props.subject || null,
        content: props.content || null,
        comment: comment.value || null,
    }));
    if (!res) return; // failed — `error` is shown in the modal

    window.$notify?.success?.({ title: 'Thanks — we’ll review it.', duration: 2500 });
    show.value = false;
}
</script>

<template>
    <span v-if="aiEnabled" class="inline-flex">
        <button
            type="button"
            class="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
            title="Report this AI result as inaccurate"
            @click="open"
        >
            <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M4 2a1 1 0 0 0-1 1v15a1 1 0 1 0 2 0v-5h9.5a1 1 0 0 0 .78-1.63L13.2 8l2.08-2.37A1 1 0 0 0 14.5 4H5V3a1 1 0 0 0-1-1Z" />
            </svg>
            Report inaccurate
        </button>

        <Modal :show="show" title="Report an inaccurate AI result" max-width="sm:max-w-lg" @onClose="show = false">
            <div class="mt-4 space-y-4 text-left">
                <p v-if="subject" class="text-sm text-slate-500">
                    Reporting: <span class="font-medium text-gray-700">{{ subject }}</span>
                </p>

                <div>
                    <p class="mb-1.5 text-sm font-medium text-gray-700">
                        What's off? <span class="font-normal text-slate-400">(optional)</span>
                    </p>
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="c in CATEGORIES"
                            :key="c.key"
                            type="button"
                            class="rounded-full border px-3 py-1 text-xs"
                            :class="category === c.key ? 'border-tb-blue bg-tb-blue/10 text-tb-blue' : 'border-gray-300 text-gray-600 hover:bg-gray-50'"
                            @click="category = category === c.key ? null : c.key"
                        >{{ c.label }}</button>
                    </div>
                </div>

                <div>
                    <label class="mb-1 block text-sm font-medium text-gray-700">
                        Tell us more <span class="font-normal text-slate-400">(optional)</span>
                    </label>
                    <textarea
                        v-model="comment"
                        rows="4"
                        placeholder="What was wrong, and what would have been right?"
                        class="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-tb-blue focus:ring-tb-blue"
                    />
                </div>

                <p v-if="error" class="rounded bg-red-50 px-3 py-2 text-xs text-red-700">{{ error }}</p>

                <div class="flex justify-end gap-2">
                    <button
                        type="button"
                        class="rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        @click="show = false"
                    >Cancel</button>
                    <button
                        type="button"
                        class="inline-flex items-center rounded-md bg-tb-blue px-4 py-2 text-xs font-semibold text-white hover:bg-tb-blue/90 disabled:opacity-60"
                        :disabled="loading"
                        @click="submit"
                    >{{ loading ? 'Submitting…' : 'Submit report' }}</button>
                </div>
            </div>
        </Modal>
    </span>
</template>
